import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { DefaultFilter } from '../../../../components/common/table/default-filter';
import ExcelButton from '../../../../components/common/table/excel-button/excel-button';
import Table from '../../../../components/common/table/table';
import { Option } from '../../../../components/form/elements/select';
import FertilizationWarehouseData from '../../../../interfaces/fertilization-plan-warehouse';
import { ListParams } from '../../../../services/http-client';
import { useService } from '../../../../services/services';
import css from './index.module.css';

export default function FertilizationPlanWarehouseTableIndex() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.fertilization-plan.warehouse.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const params = useParams<{ farmId: string; season: string }>();

  const [productTypeFilter, setProductTypeFilter] = useState<Option[]>([]);
  const [productNameFilter, setProductFilter] = useState<Option[]>([]);

  const { fertilizationPlanService } = useService();

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      fertilizationPlanService.warehouseIndex(params.farmId, params.season, {
        page,
        sort,
        desc,
        ...filter,
      }),
    [fertilizationPlanService, params.farmId, params.season]
  );

  useEffect(() => {
    const subscription = fertilizationPlanService
      .usedProducts(params.farmId, params.season)
      .subscribe((products) => {
        setProductTypeFilter(
          products
            .filter(
              (product, i) =>
                products.findIndex(
                  (product_) => product_.type_id === product.type_id
                ) === i
            ) // filter out products with repeating types
            .map((product) => {
              return { value: product.type_id, label: product.type.value };
            })
        );
        setProductFilter(
          products.map((product) => {
            return { value: product.id, label: product.name };
          })
        );
      });
    return () => subscription.unsubscribe();
  }, [fertilizationPlanService, params.farmId, params.season]);

  const toolbar = useCallback(
    (filter: ListParams) => (
      <div className={css['topbar']}>
        <ExcelButton
          onClick={() =>
            fertilizationPlanService
              .exportWarehouseXls(params.farmId, params.season, {
                ...filter.params,
              })
              .subscribe()
          }
        />
      </div>
    ),
    [fertilizationPlanService, params.farmId, params.season]
  );

  return (
    <div className={css['body']}>
      <Table<FertilizationWarehouseData>
        toolbar={toolbar}
        request={request}
        keyAccessor="product_id"
        columns={[
          {
            accessor: 'product_type_value',
            header: tp('product_type_value'),
            sortable: true,
            className: css['strong'],
            filter: (
              <DefaultFilter
                options={productTypeFilter}
                name="product_type_id"
              />
            ),
          },
          {
            accessor: 'product_name',
            header: tp('product_name'),
            sortable: true,
            className: css['strong'],
            filter: (
              <DefaultFilter options={productNameFilter} name="product_id" />
            ),
          },
          {
            accessor: 'remainder_amount',
            header: tp('remainder_amount'),
            sortable: false,
          },
          {
            accessor: 'needed_amount',
            header: tp('needed_amount'),
            sortable: false,
          },
          {
            accessor: 'missing_amount',
            header: tp('missing_amount'),
            sortable: false,
          },
        ]}
      />
    </div>
  );
}
